import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Headphones, PhoneCall, Wifi, ShieldCheck, ArrowRight, Loader2, Fingerprint, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { QuickActions } from "@/components/CallInfoPanel";

export default function Connect() {
  const { user, setUserData } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(() => localStorage.getItem("zeeptel_call_mode") || "callback");
  const [phone, setPhone] = useState(user?.callback_number || user?.phone || "");
  const verified = !!user?.own_caller_id?.verified;
  const [showMine, setShowMine] = useState(verified && user?.own_caller_id?.enabled !== false);
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(() => localStorage.getItem("zeeptel_skip_connect") === "1");

  const proceed = async () => {
    setBusy(true);
    try {
      localStorage.setItem("zeeptel_call_mode", mode);
      if (remember) localStorage.setItem("zeeptel_skip_connect", "1"); else localStorage.removeItem("zeeptel_skip_connect");
      if (mode === "callback") {
        if (phone.replace(/\D/g, "").length < 8) { toast.error("Enter the number we should call you on."); setBusy(false); return; }
        if (phone.trim() !== (user?.callback_number || user?.phone || "")) {
          const { data } = await api.put("/profile", { phone: user?.phone || phone.trim(), callback_number: phone.trim() });
          setUserData(data);
        }
      }
      if (verified && showMine !== (user?.own_caller_id?.enabled !== false)) {
        const { data } = await api.put("/caller-id/toggle", { enabled: showMine });
        setUserData({ ...user, own_caller_id: data });
      }
      navigate("/app", { state: { slideIn: true } });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const Card = ({ id, icon: Icon, title, sub, badge, children }) => (
    <button type="button" onClick={() => setMode(id)} data-testid={`connect-mode-${id}`} aria-pressed={mode === id}
      className={`w-full text-left rounded-3xl bg-white p-5 border-2 transition-all ${mode === id ? "border-primary shadow-lg shadow-primary/10" : "border-black/10 hover:border-black/20"}`}>
      <div className="flex items-start gap-3">
        <div className={`h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 ${mode === id ? "bg-primary text-white" : "bg-zinc-100 text-zinc-500"}`}><Icon className="h-5 w-5" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-extrabold text-base">{title}</div>
          <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1">{badge}{sub}</div>
        </div>
        <span className={`mt-1 h-5 w-5 rounded-full border-2 shrink-0 ${mode === id ? "border-primary bg-primary" : "border-zinc-300"}`} />
      </div>
      {children}
    </button>
  );

  return (
    <div className="mx-auto max-w-md pb-44 lg:pb-28 animate-in fade-in slide-in-from-bottom-4 duration-300" data-testid="connect-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold mt-2 mb-1">How do you want us to connect you?</h1>
      <p className="text-sm text-zinc-500 mb-4">Hi {user?.name?.split(" ")[0] || "there"} — pick how your call is placed. You can change this any time.</p>

      <div className="mb-5" data-testid="connect-quick-actions">
        <QuickActions />
      </div>

      <div className="space-y-3">
        <Card id="browser" icon={Headphones} title="Call from this device" badge={<Wifi className="h-3.5 w-3.5 text-primary" />} sub="Operates exclusively on Wi-Fi / internet · best quality" />
        <Card id="callback" icon={PhoneCall} title="Call me back" badge={<ShieldCheck className="h-3.5 w-3.5 text-primary" />} sub="Secure direct call — uses almost no data">
          {mode === "callback" && (
            <div className="mt-4" onClick={(e) => e.stopPropagation()}>
              <label className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">My phone number</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="+1 651 347 2708" data-testid="connect-callback-number"
                className="mt-1 w-full bg-zinc-50 border border-black/10 rounded-xl px-4 py-3 font-mono text-base outline-none focus:ring-2 focus:ring-primary" />
            </div>
          )}
        </Card>
      </div>

      <div className="mt-5 rounded-3xl bg-white border-2 border-black/10 p-5" data-testid="connect-callerid-box">
        <div className="font-heading font-extrabold text-base">Which number should they see?</div>
        <p className="text-xs text-zinc-500 mt-0.5 mb-3">What appears on the receiver&rsquo;s screen when you call.</p>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => verified ? setShowMine(true) : navigate("/settings")} data-testid="connect-callerid-mine" aria-pressed={showMine}
            className={`rounded-2xl border-2 p-3 text-left ${showMine && verified ? "border-primary bg-primary/5" : "border-black/10"}`}>
            <div className="flex items-center gap-1.5 font-bold text-sm"><Fingerprint className="h-4 w-4 text-primary" /> My number</div>
            <div className="text-[11px] text-zinc-500 mt-0.5 font-mono truncate">{verified ? user.own_caller_id.number : "Verify in Settings →"}</div>
          </button>
          <button type="button" onClick={() => setShowMine(false)} data-testid="connect-callerid-anon" aria-pressed={!showMine}
            className={`rounded-2xl border-2 p-3 text-left ${!showMine || !verified ? "border-primary bg-primary/5" : "border-black/10"}`}>
            <div className="flex items-center gap-1.5 font-bold text-sm"><EyeOff className="h-4 w-4 text-primary" /> Anonymous</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">A Zeeptel number is shown</div>
          </button>
        </div>
      </div>

      <label className="mt-4 flex items-center gap-3 rounded-2xl bg-white border border-black/10 px-4 py-3 cursor-pointer select-none" data-testid="connect-remember-label">
        <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="connect-remember-toggle"
          className="h-5 w-5 rounded accent-primary" />
        <span className="text-sm">
          <span className="font-bold">Remember my choice</span>
          <span className="block text-xs text-zinc-500">Skip this screen next time — you can still change it from the keypad.</span>
        </span>
      </label>

      <div className="fixed bottom-20 lg:bottom-6 left-0 right-0 px-4 z-30">
        <button onClick={proceed} disabled={busy} data-testid="connect-proceed"
          className="mx-auto max-w-md w-full h-14 rounded-full bg-primary text-white font-heading font-extrabold text-base flex items-center justify-center gap-2 shadow-xl shadow-primary/30 active:scale-[0.98] transition disabled:opacity-60">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5" />} Proceed with the call
        </button>
      </div>
      <p className="text-center text-xs text-zinc-400 mt-4"><Link to="/app" className="underline">Skip — go straight to the keypad</Link></p>
    </div>
  );
}
